import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { NumberToConvertExtended } from '@app/shared/currency.pipe';
import {
  AddItemOption,
  RemoveItemOption,
  RemoveItemOptionSuccess,
  UpdateItem,
  VatSwitchToggle,
} from '@app/store/actions/cart.action';
import { CartState } from '@app/store/state/cart.state';
import { translate } from '@ngneat/transloco';
import { Actions, ofActionCompleted, ofActionDispatched, ofActionSuccessful, Store } from '@ngxs/store';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { Item, Price } from '@app/models/item';
import { ChangeOfferRenewalWarrantyService } from '@app/shared-change-offer/renewal-warranty/change-offer-renewal-warranty.service';
import { LoaderService } from '@app/core/loader.service';

export interface IRenewalWarrantyInfo {
  is_available: boolean;
  name: string;
  prices: IRenewalWarrantyInfoPrice;
  type: string;
}

export interface IRenewalWarrantyInfoPrice {
  activation: {
    price: Price;
    price_before_discount: Price;
  };
  renew: {
    price: Price;
    price_before_discount: Price;
  };
}

@Component({
  selector: 'app-change-offer-renewal-warranty',
  templateUrl: './change-offer-renewal-warranty.component.html',
  styleUrls: ['./change-offer-renewal-warranty.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChangeOfferRenewalWarrantyComponent implements OnInit, OnChanges {
  @Input() item: Item;
  @Input() lang = '';
  @Input() renewalWarranty: IRenewalWarrantyInfo;
  @Input() isDisabled = false;

  public isChecked = true;
  public isLoading = false;

  public renewalWarrantyInfoLink = '#';

  private onDestroy$ = new Subject<boolean>();

  get activationPrice(): NumberToConvertExtended {
    return {
      amountVAT: this.renewalWarranty.prices.activation.price.incl_vat,
      amountNoVAT: this.renewalWarranty.prices.activation.price.excl_vat,
      textAfter: this.getPeriodText(),
    }
  }

  get renewPrice(): NumberToConvertExtended {
    return {
      amountVAT: this.renewalWarranty.prices.renew.price.incl_vat,
      amountNoVAT: this.renewalWarranty.prices.renew.price.excl_vat,
    }
  }

  constructor(
    private store: Store,
    private action$: Actions,
    private renewalWarrantyService: ChangeOfferRenewalWarrantyService,
    private changeDetectorRef: ChangeDetectorRef,
    private loader: LoaderService,
    private actions: Actions,
  ) {}

  ngOnInit() {
    if (!this.item.options['renewal_warranty']) {
      this.manageRWCart(true);
    }

    this.action$.pipe(ofActionDispatched(UpdateItem)).pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.isLoading = true;
    })

    this.action$.pipe(ofActionSuccessful(RemoveItemOptionSuccess)).pipe(takeUntil(this.onDestroy$)).subscribe(payload => {
      this.isChecked = false;
    });

    this.actions
      .pipe(ofActionCompleted(VatSwitchToggle))
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.changeDetectorRef.markForCheck();
      });
  }

  ngOnChanges(simpleChanges: SimpleChanges) {
    if ('lang' in simpleChanges) {
      this.lang = simpleChanges.lang.currentValue;
      this.setRenewalWarrantyLink();
    }

    if ('item' in simpleChanges) {
      this.loadRenewalWarrantyInfos(simpleChanges.item.currentValue);
    }
  }

  toggleCheckbox(matCheckboxChange: MatCheckboxChange) {
    this.isLoading = true;
    const bool = matCheckboxChange.checked;

    this.manageRWCart(bool);
  }

  manageRWCart(bool: boolean) {
    if (bool) {
      if (!this.item?.options['renewal_warranty']) {
        this.loader.item.set(true);
        this.store.dispatch(new AddItemOption(this.item, 'renewal_warranty'))
          .pipe(take(1))
          .subscribe(() => {
          this.loader.item.set(false);
        });
      }
    } else {
      if (this.item?.options['renewal_warranty']) {
        this.loader.item.set(true);
        this.store.dispatch(new RemoveItemOption(this.item, 'renewal_warranty'))
          .pipe(take(1))
          .subscribe(() => {
          this.loader.item.set(false);
        });
      }
    }
  }

  setRenewalWarrantyLink() {
    switch (this.lang) {
      case 'fr':
        this.renewalWarrantyInfoLink = 'https://www.infomaniak.com/fr/domaines/renewal-warranty';
        break;
      case 'de':
        this.renewalWarrantyInfoLink = 'https://www.infomaniak.com/de/domains/renewal-warranty';
        break;
      case 'en':
        this.renewalWarrantyInfoLink = 'https://www.infomaniak.com/en/domains/renewal-warranty';
        break;
      case 'it':
        this.renewalWarrantyInfoLink = 'https://www.infomaniak.com/it/domini/renewal-warranty';
        break;
      case 'es':
        this.renewalWarrantyInfoLink = 'https://www.infomaniak.com/es/dominios/renewal-warranty';
        break;
      default:
        this.renewalWarrantyInfoLink = 'https://www.infomaniak.com/fr/domaines/renewal-warranty';
    }
  }

  private getPeriodText(): string {
    switch (this.item.attributes.period) {
      case 1:
        return ' ' + translate('/ mois');
      case 3:
        return ' ' + translate('/ trimestre');
      case 12:
        return ' ' + translate('/ an');
      default:
        return '';
    }
  }

  private loadRenewalWarrantyInfos(item: Item) {
    this.isLoading = true;
    this.renewalWarrantyService
      .getRenewalWarrantyInfo(
        this.store.selectSnapshot(CartState.getCart),
        item.setup.id
      )
      .subscribe((renewalWarrantyInfos) => {
        this.renewalWarranty = renewalWarrantyInfos;
        this.changeDetectorRef.markForCheck();
        this.isLoading = false;
      });
  }
}
